import { IDocumentCategory } from 'types/order/documents/IDocumentCategoryType';
import { eDocumentVisibilityType } from '../../../types/order/documents/eDocumentVisibilityType';
import {
  convertFromUpdatedDocumentHistoryResponseToUpdatedVersionArray,
  convertFromUpdatedDocumentResponseToUpdatedLatestDocumentVersionArray,
  IMetadata,
  IStandardDocumentVersionData,
  IUpdatedDocumentResponse,
} from '../../../types/order/documents/shared/interfaces';
import { IUploadDocument } from '../../../types/order/documents/upload/interfaces';
import { baseUrlService } from '../../../ui-toolkit/rest/BaseUrlService';
import { Logger } from '../../../ui-toolkit/logging/LoggerService';
import { APIFactory } from '../../../ui-toolkit/rest/APIFactory';
import { axiosErrorHandler } from '../../../ui-toolkit/rest/axiosErrorHandler';

export const getDocumentsAsync = async (
  orderId: string,
  categories?: IDocumentCategory[]
): Promise<IStandardDocumentVersionData[]> =>
  APIFactory(baseUrlService.ApiLocation)
    .get<IUpdatedDocumentResponse[]>(`/timber/api/internal/orders/${orderId}/documents/all`)
    .then((response) =>
      convertFromUpdatedDocumentResponseToUpdatedLatestDocumentVersionArray(response.data, categories)
    )
    .catch(axiosErrorHandler);

export const getDocumentHistoryAsync = async (documentId: string): Promise<IStandardDocumentVersionData[]> =>
  APIFactory(baseUrlService.ApiLocation)
    .get<IUpdatedDocumentResponse>(`/timber/api/internal/documents/${documentId}`)
    .then((response) => {
      return convertFromUpdatedDocumentHistoryResponseToUpdatedVersionArray(response.data);
    })
    .catch(axiosErrorHandler);

export const createDownloadDocumentUrl = (documentId: string, versionId: string) => {
  return `${baseUrlService.ApiLocation}/timber/api/internal/documents/${documentId}/versions/${versionId}/content`;
};

export const createDownloadLatestDocumentUrl = (standardDocId: string) => {
  return `${baseUrlService.ApiLocation}/timber/api/internal/documents/${standardDocId}/versions/latest/content`;
};

export const deleteDocumentAsync = async (documentId: string, versionId: string): Promise<any> =>
  APIFactory(baseUrlService.ApiLocation)
    .delete<{}>(`/timber/api/internal/documents/${documentId}/versions/${versionId}`)
    .catch(axiosErrorHandler);

export const mergeDocumentsAsync = async (
  orderId: string,
  documentIds: string[],
  fileName: string | undefined,
  category: IDocumentCategory
): Promise<any> =>
  APIFactory(baseUrlService.ApiLocation)
    .post<{}>(`/timber/api/internal/documents/merge`, {
      orderId,
      documentIds,
      label: fileName,
      outputCategory: category?.name,
    })
    .catch(axiosErrorHandler);

export const publishDocumentHistoryAsync = async (
  documentId: string,
  versionId: string,
  visibilityType: eDocumentVisibilityType
): Promise<any> =>
  APIFactory(baseUrlService.ApiLocation)
    .post<{}>(`/timber/api/internal/documents/${documentId}/versions/${versionId}/publish`, {
      standardDocumentVisibilityType: visibilityType,
    })
    .catch(axiosErrorHandler);

export const unpublishDocumentHistoryAsync = async (
  documentId: string,
  versionId: string,
  visibilityType: eDocumentVisibilityType
): Promise<any> =>
  APIFactory(baseUrlService.ApiLocation)
    .post<{}>(`/timber/api/internal/documents/${documentId}/versions/${versionId}/unpublish`, {
      standardDocumentVisibilityType: visibilityType,
    })
    .catch(axiosErrorHandler);

export const publishDocumentAsync = async (documentId: string, visibilityType: eDocumentVisibilityType): Promise<any> =>
  APIFactory(baseUrlService.ApiLocation)
    .post<{}>(`/timber/api/internal/documents/${documentId}/versions/latest/publish`, {
      standardDocumentVisibilityType: visibilityType,
    })
    .catch(axiosErrorHandler);

export const unpublishDocumentAsync = async (
  documentId: string,
  visibilityType: eDocumentVisibilityType
): Promise<any> =>
  APIFactory(baseUrlService.ApiLocation)
    .post<{}>(`/timber/api/internal/documents/${documentId}/versions/latest/unpublish`, {
      standardDocumentVisibilityType: visibilityType,
    })
    .catch(axiosErrorHandler);

export const updateDocumentAsync = async (
  documentId: string,
  versionId: string,
  value: { funding?: boolean; requiredAtSigning?: boolean }
): Promise<any> =>
  APIFactory(baseUrlService.ApiLocation)
    .patch<{}>(`/timber/api/internal/documents/${documentId}/versions/${versionId}`, value)
    .catch(axiosErrorHandler);

// This is to fix using the admin documents api on the client lender page
export const v4UploadDocsAsync = async (orderId: string, document: IUploadDocument): Promise<any> => {
  const formData = new FormData();
  formData.append('file', document.file);
  formData.append('file_name', document.file.name);
  if (document.metadata) {
    formData.append(
      'json',
      JSON.stringify({
        ...document.metadata,
        original_file_name: document.file.name,
      })
    );
  }

  return APIFactory(baseUrlService.ApiLocation)
    .post<string>(
      `/documents/v4/${orderId}/${document.category}/${!document.isRepeatable ? 'versioned' : 'unversioned'}/upload`,
      formData
    )
    .then((response) => response.data)
    .catch(axiosErrorHandler);
};

export const uploadDocumentAsync = async (
  orderId: string,
  document: IUploadDocument,
  existingDocument: IStandardDocumentVersionData | undefined
): Promise<string> => {
  const formData = new FormData();
  // New API Required Fields
  formData.append('file', document.file);
  formData.append('category', document.category as string);
  formData.append('contentType', document.file.type);
  formData.append('originalFilename', document.file.name);
  formData.append('label', document.file.name);
  if (document.metadata) {
    formData.append(
      'jsonMetadata',
      JSON.stringify({
        ...document.metadata,
      })
    );
  }

  // Handle new upload API conditions
  if (
    existingDocument &&
    (!document.isRepeatable ||
      (document.isRepeatable && existingDocument?.metadata?.original_file_name === document.file.name))
  ) {
    return APIFactory(baseUrlService.ApiLocation)
      .post<{ document: { id: string } }>(`/timber/api/internal/documents/${existingDocument.id}/versions`, formData)
      .then((response) => response.data.document.id)
      .catch(axiosErrorHandler);
  } else {
    return APIFactory(baseUrlService.ApiLocation)
      .post<{ id: string }>(`/timber/api/internal/orders/${orderId}/documents`, formData)
      .then((response) => response.data.id)
      .catch(axiosErrorHandler);
  }
};

export const updateDocumentMetadataAsync = async (id: string, metadata: IMetadata) => {
  try {
    const result = await APIFactory(baseUrlService.ApiLocation).put(`/documents/v5/${id}`, metadata);
    return result.data;
  } catch (e: any) {
    Logger.error('Document Update Error', e);
    throw e;
  }
};
